import type { FieldValuesV1, HitV1 } from '@ttab/elephant-api/index'
import { format, parseISO } from 'date-fns'
import { toZonedTime } from 'date-fns-tz'
import { assignmentTypes } from '../../../assignmentTypes.js'
import type { AssignmentType } from '../../../assignmentTypes.js'
import { indexDay } from './indexDay.js'
import type { MemoAssignment, MemoItem } from './renderMemo.js'
import type { MemoSourceType } from './schema.js'

type Fields = Record<string, FieldValuesV1 | undefined>

function value(hit: HitV1, field: string): string | undefined {
  const values = (hit.fields as Fields)[field]?.values

  return values?.find((entry) => !!entry) || undefined
}

/**
 * Pick the text of a multi-role meta block, e.g. the public description out of a
 * document that also carries an internal one.
 *
 * The index returns `role` as its own field, positionally parallel to
 * `data.text`, which is why this cannot go through `value()` - that skips empty
 * entries and so destroys the alignment the two arrays depend on. Empty blocks
 * are stripped on save, so a document with an empty public and a filled internal
 * description can come back with one text and two roles; misaligned arrays are
 * not worth guessing at and yield nothing.
 */
function valueByRole(hit: HitV1, field: string, role: string): string | undefined {
  const fields = hit.fields as Fields
  const texts = fields[`${field}.data.text`]?.values
  const roles = fields[`${field}.role`]?.values

  if (!texts?.length || texts.length !== roles?.length) {
    return undefined
  }

  return texts[roles.indexOf(role)] || undefined
}

/**
 * Index fields the memo candidate queries ask for, beyond what the shared
 * document schemas request. The assignment description carries the
 * "Fullängdstext med fakta" style text; when the index does not return it the
 * corresponding template line is simply skipped.
 *
 * The role comes along with it because an assignment can carry an internal
 * description as well as a public one, and only the public one belongs in a
 * memo that goes out to customers.
 */
export const memoExtraFields = [
  'document.meta.core_assignment.title',
  'document.meta.core_assignment.meta.core_description.data.text',
  'document.meta.core_assignment.meta.core_description.role',
  'document.meta.core_assignment.meta.core_assignment_type.value'
] as const

/**
 * Extra fields that only make sense for one source. Events carry the public
 * description a calendar listing prints and the granularity that says whether
 * they happen at a time of day at all; asking for either on an article or a
 * planning query would be noise.
 */
export const memoSourceExtraFields: Record<MemoSourceType, readonly string[]> = {
  // A planning carries its own public description, the sentence that says what
  // is happening ("Ebba Busch håller sommartal på Hönö"), alongside the
  // assignment's - and only the public one belongs in a memo.
  planning: [
    'document.meta.core_description.data.text',
    'document.meta.core_description.role'
  ],
  article: [],
  // A print article's own fields are its source's, listed with the query - none
  // of the shared memo extras apply to one.
  'print-article': [],
  event: [
    'document.meta.core_event.data.dateGranularity',
    'document.meta.core_description.data.text',
    'document.meta.core_description.role'
  ]
}

/** The assignment type a `core/assignment-type` block names, if it names one. */
function toAssignmentType(value: string | undefined): AssignmentType | undefined {
  return assignmentTypes.find((type) => type === value)
}

/**
 * The type an assignment's `core/assignment-type` blocks name together. Picture
 * and video is saved as one block of each, which the type picker reads sorted and
 * joined as "picture/video"; a combination it does not know falls back on the
 * first type.
 */
function assignmentTypeOf(values: (string | undefined)[]): AssignmentType | undefined {
  const combined = toAssignmentType(
    values.filter((value) => !!value).sort().join('/')
  )

  return combined ?? toAssignmentType(values[0])
}

/**
 * The assignments a planning carries, in the order the planning carries them -
 * the order the editor put them in.
 *
 * Read off the loaded document rather than the flat index fields. The index
 * returns one array of assignment titles and one of assignment types, and
 * pairing them positionally is the misalignment `valueByRole` refuses to guess
 * at - only here it would print one assignment's title under another's label,
 * in a memo that goes out to customers. `memoQueryParams` loads the whole
 * document for planning queries for this reason; a hit without one simply
 * carries no assignments.
 *
 * Each carries its own `tt/slugline`, which is what its line is slugged by.
 */
function assignmentsOf(hit: HitV1): MemoAssignment[] {
  return (hit.document?.meta ?? [])
    .filter((block) => block.type === 'core/assignment')
    .map((block) => {
      const slugline = block.meta
        .find((meta) => meta.type === 'tt/slugline')?.value?.trim()

      return {
        type: assignmentTypeOf(block.meta
          .filter((meta) => meta.type === 'core/assignment-type')
          .map((meta) => meta.value)),
        title: block.title,
        ...(slugline ? { slugline } : {})
      }
    })
    .filter((assignment): assignment is MemoAssignment => !!assignment.type)
}

/** An event lasting longer than this is a whole day one, not one with a time. */
const WHOLE_DAY_HOURS = 12

function toDate(iso: string | undefined, timeZone: string): Date | undefined {
  if (!iso) {
    return undefined
  }

  const date = toZonedTime(parseISO(iso), timeZone)

  if (Number.isNaN(date.getTime())) {
    return undefined
  }

  return date
}

/**
 * The time of day to print for an item, or undefined when it has none.
 *
 * `dateGranularity` is the authoritative signal, either way. Without it, an event
 * is whole day when it starts at local midnight and runs longer than twelve hours,
 * which is how the event form saves a Heldag one. Neither alone says it: events
 * really do start at 00:00, and a timed event can span several days.
 */
function toStartTimeLabel(hit: HitV1, start: Date, timeZone: string): string | undefined {
  const granularity = value(hit, 'document.meta.core_event.data.dateGranularity')

  if (granularity === 'date') {
    return undefined
  }

  // TT writes a time of day with a full stop, "07.00", never a colon.
  const label = format(start, 'HH.mm')

  if (granularity === 'datetime' || label !== '00.00') {
    return label
  }

  const end = toDate(value(hit, 'document.meta.core_event.data.end'), timeZone)

  if (end && (end.getTime() - start.getTime()) / 3_600_000 > WHOLE_DAY_HOURS) {
    return undefined
  }

  return label
}

/**
 * Map a print article hit to a memo item.
 *
 * Its headline is the first of its texts. The print article list picks the
 * `heading-1` instead, but a print article on its way into the paper is written
 * without roles on its text blocks and the headline is simply the one it leads
 * with. `document.title` is no substitute: that is the name the article carries
 * within its flow, a page slot rather than anything editorial, so an article
 * with no text at all is left out of the memo rather than listed under it.
 *
 * A print article has no section, newsvalue or slugline, and it is made for a
 * print date rather than a time of day, so there is none to print either. That
 * date is read the way every other index date is - the day it names, whether the
 * index hands it back as a day or as the midnight instant it stands for.
 */
function toPrintArticleItem(hit: HitV1, blockId: string, timeZone: string): MemoItem | undefined {
  const headline = value(hit, 'document.content.core_text.data.text')

  if (!headline) {
    return undefined
  }

  const printDate = value(hit, 'document.meta.tt_print_article.data.date')

  return {
    id: hit.id,
    documentId: hit.id,
    blockId,
    sourceType: 'print-article',
    headline,
    startDate: printDate ? indexDay(printDate, timeZone) : undefined
  }
}

/**
 * Map an index hit to a memo item.
 *
 * The headline prefers the planning/article/event's own title, which is the
 * real editorial headline, and falls back to the assignment title only when
 * the document itself has none - the assignment title is free text editors
 * type per production task (e.g. "fullängdstext kl 13", "notis") and is often
 * not fit to print in a memo.
 *
 * The assignment description is taken by role: an assignment often carries an
 * internal description alongside the public one, and the internal one is a note
 * between editors that must never reach a memo. An assignment with nothing but
 * an internal description therefore contributes no description at all.
 *
 * The day and the time of day are resolved here, in the registry timezone, so
 * that rendering a memo stays a matter of substituting strings.
 */
export function toMemoItem(
  hit: HitV1,
  sourceType: MemoSourceType,
  blockId: string,
  timeZone: string
): MemoItem | undefined {
  if (sourceType === 'print-article') {
    return toPrintArticleItem(hit, blockId, timeZone)
  }

  const headline = value(hit, 'document.title')
    || value(hit, 'document.meta.core_assignment.title')

  if (!headline) {
    return undefined
  }

  const startTime = value(hit, 'document.meta.core_event.data.start')
    || value(hit, 'heads.usable.created')
  const start = toDate(startTime, timeZone)

  // A planning is planned on a date rather than an instant, and carries neither
  // of the two above - without it every planning would fall outside the days a
  // day grouped memo is built from. A date is also no time of day, so there is
  // no label to go with it.
  //
  // The day it names is what a memo wants, and that is not always how the field
  // arrives: the document writes a bare day and the index hands the same field
  // back as midnight UTC. Carried on as it arrived, the instant reached the day
  // heading as a day key, where "2026-09-14T00:00:00Z" split on its dashes
  // yields no number to read a month off - "Invalid Date den Invalid Date" -
  // and made a group of its own beside the events of that very
  // day. See `indexDay`, which is the same reading `resolveMemoDay` gives the
  // planning a memo is filed into.
  const plannedDate = value(hit, 'document.meta.core_planning_item.data.start_date')
  const plannedDay = plannedDate ? indexDay(plannedDate, timeZone) : undefined
  const assignments = assignmentsOf(hit)

  return {
    id: hit.id,
    documentId: hit.id,
    blockId,
    sourceType,
    headline,
    sectionUuid: value(hit, 'document.rel.section.uuid'),
    sectionTitle: value(hit, 'document.rel.section.title'),
    newsvalue: value(hit, 'document.meta.core_newsvalue.value'),
    slugline: value(hit, 'document.meta.tt_slugline.value'),
    description: valueByRole(
      hit, 'document.meta.core_assignment.meta.core_description', 'public'
    ),
    publicDescription: valueByRole(hit, 'document.meta.core_description', 'public'),
    // Left out entirely rather than carried as an empty list, the way every
    // other absent field on an item is.
    ...(assignments.length ? { assignments } : {}),
    startTime: startTime ?? plannedDate,
    // The planned date wins over a publication instant: a planning is in a memo
    // for the day it is planned for, not the day the document was made usable.
    // A planned date naming no day at all is no day to prefer, and falls back to
    // the publication day the way a planning carrying none does.
    startDate: plannedDay ?? (start ? format(start, 'yyyy-MM-dd') : undefined),
    // Only an event happens at a time of day. A planning and an article have
    // `heads.usable.created` as their start - the instant the document was
    // published - which says nothing about when the thing itself is, and a memo
    // printing it would read as though the planning happened then.
    startTimeLabel: sourceType === 'event' && start
      ? toStartTimeLabel(hit, start, timeZone)
      : undefined
  }
}

export function toMemoItems(
  hits: HitV1[],
  sourceType: MemoSourceType,
  blockId: string,
  timeZone: string
): MemoItem[] {
  return hits
    .map((hit) => toMemoItem(hit, sourceType, blockId, timeZone))
    .filter((item): item is MemoItem => !!item)
}
